import i18n from '@/utils/i18n';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import { ApolloError, NetworkStatus } from '@apollo/client';
import { useCallback, useMemo, useRef } from 'react';
import { useGeneratedSalesPortalActivityNotesQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-activity-notes.generated';

export const ACTIVITY_NOTES_LIMIT = 20;

interface ActivityNotesLoaderParams {
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

interface ActivityNotesLoaderResult {
  activityNotes: ActivityNote[];
  error: ApolloError | undefined;
  fetchingMore: boolean;
  fetchMore: () => Promise<void>;
  loading: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Loads the activity notes feed for the account and prospect detail screens.
 *
 * @param params.restaurantUUID - The UUID of the restaurant.
 * @param params.skip - Set to true to skip the query.
 * @param params.supplierUUID - The UUID of the supplier.
 * @returns The notes, the error of the first page, two flags and two actions.
 *   `loading` is true for the first page and for a refetch. `fetchingMore` is
 *   true while the next page loads. `fetchMore` appends the next page. It does
 *   nothing while a page loads or after the last page. A failed page keeps the
 *   loaded notes. `refetch` loads the first page again.
 */
export const useActivityNotesLoader = ({
  restaurantUUID,
  skip,
  supplierUUID,
}: ActivityNotesLoaderParams): ActivityNotesLoaderResult => {
  const { data, error, fetchMore, networkStatus, refetch } =
    useGeneratedSalesPortalActivityNotesQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: () => 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        pageSize: ACTIVITY_NOTES_LIMIT,
        restaurantUUID,
        supplierUUID,
      },
    });

  const page = data?.getCrmActivities;
  const nextCursor = page?.cursor ?? null;

  const activityNotes = useMemo(
    () =>
      (page?.activities ?? []).map(
        (activity): ActivityNote => ({
          authorName: activity.created_by_name ?? null,
          content:
            activity.note?.content ??
            i18n.t('sales_portal.activity_notes.no_note'),
          createdAt: activity.created_at,
          interactions: activity.check_in?.interactions ?? null,
          reminderDate: activity.reminder?.remind_at ?? null,
          uuid: activity.uuid,
        }),
      ),
    [page],
  );

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  // Apollo updates networkStatus on the next render. Two scroll events in one
  // tick can both pass the isFetchingMore check. The ref blocks the second one.
  const fetchingRef = useRef(false);

  const handleRefetch = useCallback(() => refetch().catch(() => {}), [refetch]);

  const handleFetchMore = useCallback(async () => {
    if (fetchingRef.current || isFetchingMore || !nextCursor) {
      return;
    }
    fetchingRef.current = true;
    try {
      await fetchMore({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevResult;
          }

          return {
            ...prevResult,
            getCrmActivities: {
              ...fetchMoreResult.getCrmActivities,
              activities: [
                ...prevResult.getCrmActivities.activities,
                ...fetchMoreResult.getCrmActivities.activities,
              ],
            },
          };
        },
        variables: {
          cursor: nextCursor,
          pageSize: ACTIVITY_NOTES_LIMIT,
          restaurantUUID,
          supplierUUID,
        },
      });
    } catch {
      // A failed page keeps the loaded notes. The next scroll retries.
    } finally {
      fetchingRef.current = false;
    }
  }, [fetchMore, isFetchingMore, nextCursor, restaurantUUID, supplierUUID]);

  return {
    activityNotes,
    error,
    fetchingMore: isFetchingMore,
    fetchMore: handleFetchMore,
    loading:
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.refetch,
    refetch: handleRefetch,
  };
};
